import { useEffect, useState } from 'react';
import { api } from '../api';

/**
 * useTranscriptUpgrade — single source of truth for the
 * full-coverage (Google) transcript upgrade flow.
 *
 * The AssemblyAI flow is consent-modal-driven and lives in
 * UpgradeTranscriptModal; this hook owns the simpler Google
 * transcript flow (Cloud Speech / Gemini full-coverage rerun) +
 * the case-level background-job poll that watches for completion.
 *
 * Both DocumentDetailPanel (inventory dropdown) and DocumentViewer
 * (full-screen viewer) use this hook so the state machine,
 * window.confirm cost prompt, and dispatch behavior stay identical
 * across surfaces.
 *
 * Usage:
 *   const upgrade = useTranscriptUpgrade({
 *     docId, caseId,
 *     onTranscriptInvalidated: () => setTranscriptData(null),
 *     onDocChange,
 *   });
 *   <ImproveTranscriptCard
 *     onRunFullCoverage={upgrade.handleFullTranscribe}
 *     fullTranscribing={upgrade.transcribing}
 *     transcribeMsg={upgrade.transcribeMsg}
 *     ...
 *   />
 */
export default function useTranscriptUpgrade({
  docId,
  caseId,
  onTranscriptInvalidated,
  onDocChange,
}) {
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeMsg, setTranscribeMsg] = useState(null);
  const [transcribeJobId, setTranscribeJobId] = useState(null);

  async function handleFullTranscribe() {
    setTranscribing(true);
    setTranscribeMsg(null);
    try {
      const est = await api.post(`/processing/documents/${docId}/transcribe-full?estimate=true`);
      if (est.already_complete) {
        setTranscribeMsg('Already fully transcribed.');
        setTranscribing(false);
        return;
      }
      const dur = est.duration_seconds || 0;
      const cur = est.current_coverage_percentage || 0;
      const costCents = est.estimated_cost_cents || 0;
      const ok = window.confirm(
        `Full transcription will process ${Math.round(dur)}s of media.\n` +
        `Current coverage: ${Math.round(cur)}%\n` +
        `Estimated cost: ${(costCents / 100).toFixed(2)}¢\n\nProceed?`
      );
      if (!ok) { setTranscribing(false); return; }
      const result = await api.post(`/processing/documents/${docId}/transcribe-full?estimate=false`);

      // Background path: the dispatch returned. Real work is still
      // running. Track the job_id and let the polling effect below
      // surface the actual result.
      if (result?.mode === 'background' && result?.job_id) {
        setTranscribeJobId(result.job_id);
        setTranscribeMsg('Transcription started — the transcript will refresh automatically when it finishes (typically 1-3 min).');
        return;  // keep transcribing=true; the poll effect clears it
      }

      // Inline path (small audio that fit the sync API window).
      if (result?.success) {
        setTranscribeMsg('Transcription complete.');
        onTranscriptInvalidated?.();
      } else {
        setTranscribeMsg(`Failed: ${result?.error || 'Unknown error'}`);
      }
    } catch (err) {
      setTranscribeMsg(`Error: ${err.message}`);
    } finally {
      // Background path returned early and intentionally left
      // transcribing=true so the button stays disabled while the LRO
      // runs. Only the inline path falls through here.
      if (!transcribeJobId) setTranscribing(false);
    }
  }

  // Poll the case-level background-jobs feed for the transcribe job
  // we dispatched. When it leaves the active feed (success or fail),
  // refresh the transcript view.
  useEffect(() => {
    if (!transcribeJobId || !caseId) return;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const data = await api.get(`/exploration/case/${caseId}/background-jobs`);
        if (cancelled) return;
        const job = (data?.jobs || []).find(j => j.job_id === transcribeJobId);
        if (!job) {
          // Vanished from feed — finished (success or fail).
          setTranscribeJobId(null);
          setTranscribing(false);
          setTranscribeMsg('Transcription complete.');
          onTranscriptInvalidated?.();
          onDocChange?.();
          return;
        }
        if (job.status === 'failed') {
          setTranscribeJobId(null);
          setTranscribing(false);
          setTranscribeMsg(`Failed: ${job.error || 'unknown error'}`);
          return;
        }
        if (job.status === 'completed') {
          setTranscribeJobId(null);
          setTranscribing(false);
          setTranscribeMsg('Transcription complete.');
          onTranscriptInvalidated?.();
          onDocChange?.();
        }
      } catch {
        // non-fatal — keep polling
      }
    }, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [transcribeJobId, caseId, onTranscriptInvalidated, onDocChange]);

  return {
    transcribing,
    transcribeMsg,
    handleFullTranscribe,
  };
}
